<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  digit: { type: Number, default: 0 },
  // A symbol instead of a digit: '.' for a thousands separator, '-' when the number is not available.
  symbol: { type: String, default: null },
  // Neon is lit: the digit flickers on and now and then flickers briefly.
  lit: { type: Boolean, default: false },
  // The number is already on screen, so a digit that appears later switches on by itself.
  rollIn: { type: Boolean, default: false },
  // Position in the number: changing digits switch one after the other from left to right.
  index: { type: Number, default: 0 },
})

const SWITCH_OFF_MS = 280

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

const shown = ref(props.digit)
const char = computed(() => props.symbol ?? shown.value)
// null: normal | 'out': the old digit sputters off | 'in': the new digit sputters on
const phase = ref(props.rollIn && !reducedMotion ? 'in' : null)
let timer

// A changed digit sputters off, the new one sputters on in its place, like a neon sign being re-wired.
function change() {
  clearTimeout(timer)
  if (reducedMotion || !props.lit) {
    shown.value = props.digit
    return
  }
  phase.value = 'out'
  timer = setTimeout(
    () => {
      shown.value = props.digit
      phase.value = 'in'
    },
    SWITCH_OFF_MS + props.index * 100
  )
}

watch(() => props.digit, change)
onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <span
    class="neon-digit"
    :class="{
      'neon-digit--dot': symbol === '.',
      'neon-digit--lit': lit,
      'neon-digit--out': phase === 'out',
      'neon-digit--in': phase === 'in',
    }"
    :style="{ '--i': index }"
    aria-hidden="true"
  >
    <span class="neon-digit__face" :data-digit="char">{{ char }}</span>
  </span>
</template>

<style scoped>
.neon-digit {
  --glow: drop-shadow(0 0 0.02em rgb(var(--q-red))) drop-shadow(0 0 0.08em rgb(var(--q-red)));
  --flare: drop-shadow(0 0 0.03em #fff) drop-shadow(0 0 0.1em rgb(var(--q-red)))
    drop-shadow(0 0 0.22em rgb(var(--q-red)));

  position: relative;
  display: inline-block;
  width: 0.4em;
  height: 1.1em;
  vertical-align: top;
  text-align: center;
}

.neon-digit--dot {
  width: 0.2em;
}

/* The unlit glass tube: a dim outline. */
.neon-digit__face {
  position: absolute;
  top: 0.1275em;
  left: 0;
  width: 100%;
  height: 0.845em;
  line-height: 0.845em;
  color: transparent;
  -webkit-text-stroke: 0.025em rgb(255 255 255 / 22%);
}

/* The lit neon: bright outline with glow. Its opacity is what flickers. */
.neon-digit__face::after {
  content: attr(data-digit);
  position: absolute;
  inset: 0;
  color: transparent;
  -webkit-text-stroke: 0.025em #fff;
  filter: var(--glow);
  opacity: 0;
}

/* Each digit sputters on at its own moment, then now and then one flickers briefly. */
.neon-digit--lit .neon-digit__face::after {
  --ignite-delay: calc(var(--i) * 0.17s);

  animation:
    neon-ignite 1.6s linear var(--ignite-delay) both,
    neon-idle calc(16s + var(--i) * 3.3s) linear calc(var(--ignite-delay) + 1.6s) infinite;
}

/* The old digit sputters off. */
.neon-digit--lit.neon-digit--out .neon-digit__face::after {
  animation: neon-off 0.28s linear forwards;
}

/* The new digit sputters on with a flare of light, then settles into the normal glow. */
.neon-digit--lit.neon-digit--in .neon-digit__face::after {
  animation:
    neon-on 1.1s linear both,
    neon-idle calc(16s + var(--i) * 3.3s) linear 1.1s infinite;
}

@keyframes neon-ignite {
  0% {
    opacity: 0;
  }

  6% {
    opacity: 0.9;
  }

  8% {
    opacity: 0;
  }

  15% {
    opacity: 0;
  }

  16% {
    opacity: 1;
  }

  19%,
  24% {
    opacity: 0.15;
  }

  26% {
    opacity: 1;
  }

  30% {
    opacity: 0.45;
  }

  34%,
  55% {
    opacity: 1;
  }

  57% {
    opacity: 0.7;
  }

  59%,
  100% {
    opacity: 1;
  }
}

@keyframes neon-off {
  0% {
    opacity: 1;
  }

  20% {
    opacity: 0.2;
  }

  38% {
    opacity: 0.9;
  }

  60% {
    opacity: 0.1;
  }

  75% {
    opacity: 0.5;
  }

  100% {
    opacity: 0;
  }
}

@keyframes neon-on {
  0% {
    opacity: 0;
    filter: var(--flare);
  }

  10% {
    opacity: 1;
    filter: var(--flare);
  }

  16% {
    opacity: 0.15;
    filter: var(--flare);
  }

  26% {
    opacity: 1;
    filter: var(--flare);
  }

  32% {
    opacity: 0.5;
    filter: var(--flare);
  }

  42% {
    opacity: 1;
    filter: var(--flare);
  }

  100% {
    opacity: 1;
    filter: var(--glow);
  }
}

/* A rare, soft dip in brightness. */
@keyframes neon-idle {
  0%,
  92%,
  100% {
    opacity: 1;
  }

  93% {
    opacity: 0.8;
  }

  94% {
    opacity: 1;
  }

  95% {
    opacity: 0.88;
  }

  96% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .neon-digit--lit .neon-digit__face::after {
    animation: none;
    opacity: 1;
  }
}
</style>
